import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createAccountApi, createAccountFromContactsApi, createAccountDealApi, createAccountDealNoteApi, createConversationThread, createCustomAccountQueryApi, deleteAccountApi, deleteAccountDealApi, deleteAccountDealNoteApi, deleteConversationThread, dismissFollowUpSuggestionApi, fetchAccountActionPlanApi, fetchAccountDashboardApi, fetchAccountDealNotesApi, fetchAccountDealsApi, fetchAccountRevenueApi, fetchAccountsApi, fetchChatApi, fetchCompanyInfoApi, fetchConversationThreadMessages, fetchConversationThreads, fetchCustomAccountQueriesApi, fetchFollowUpAlertApi, getChatAnswerApi, renameConversationThread, runCustomAccountQueryApi, selectCustomAccountQueryApi, sendConversationChatMessage, setFollowUpAlertApi, updateAccountActionPlanApi, updateAccountDealApi, updateAccountDealNoteApi, updateAccountStatusApi, whitespacingAccountActionPlanApi, logWhitespaceOutcomeApi, updateWhitespaceVendorsApi } from "../api/accountsApi";
import { showSuccess, showErrorFromMutation } from "@/shared/utils/toast";
import { convertCurrenciesDeep } from "@/shared/utils/helperFunctions";

// fetch accounts hook
export const useFetchAccounts = ({userId = "", enabled = true}) => {
    return useQuery({
    queryKey: ["accounts"],
    queryFn: async () => {
      const data = await fetchAccountsApi(userId);
      return convertCurrenciesDeep(data);
    },
    enabled: !!userId && enabled,
    // Was refetchOnMount: "always". ["accounts"] is invalidated from the mutations
    // here plus the enrollment tracker, so writes still surface immediately - this
    // only stops a full re-download on every visit to the screen.
    staleTime: 30_000,
  });
};

export const useFetchCustomAccountQueries = ({ userId = "", enabled = true } = {}) =>
  useQuery({
    queryKey: ["custom-account-queries", userId],
    queryFn: async () => {
      const response = await fetchCustomAccountQueriesApi();
      const data = Array.isArray(response) ? response : [];
      console.info(
        "[CustomAccountQueries][frontend] fetched statuses",
        JSON.stringify(
          data.map((query) => ({
            queryId: query?.id,
            status: query?.last_run_status,
            selected: query?.is_selected === true,
            recommendations: Array.isArray(query?.recommendations)
              ? query.recommendations.length
              : 0,
            evidenceLinks: Array.isArray(query?.recommendations)
              ? query.recommendations.reduce(
                  (count, account) => count + (Array.isArray(account?.evidence)
                    ? account.evidence.filter((item) => item?.source_url).length
                    : 0),
                  0,
                )
              : 0,
          })),
        ),
      );
      return data;
    },
    enabled: Boolean(userId) && enabled,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasActiveRun = Array.isArray(data)
        && data.some((item) => ["queued", "running"].includes(item?.last_run_status));
      return hasActiveRun ? 2_000 : false;
    },
  });

export const useCreateCustomAccountQuery = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomAccountQueryApi,
    // Awaited so the mutation stays pending until the list shows the new query as
    // queued; otherwise the old results flash between the request and the refetch.
    onSuccess: async (data) => {
      showSuccess("Custom query saved. Research has started.");
      await queryClient.invalidateQueries({ queryKey: ["custom-account-queries"] });
      action?.(data);
    },
    onError: (error) => showErrorFromMutation(error, "Failed to save custom query"),
  });
};

export const useRunCustomAccountQuery = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runCustomAccountQueryApi,
    // Awaited for the same reason: stay pending until the refetched status says queued.
    onSuccess: async (data) => {
      showSuccess("Custom query research queued.");
      await queryClient.invalidateQueries({ queryKey: ["custom-account-queries"] });
      action?.(data);
    },
    onError: (error) => showErrorFromMutation(error, "Failed to run custom query"),
  });
};

export const useSelectCustomAccountQuery = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: selectCustomAccountQueryApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["custom-account-queries"] });
      action?.(data);
    },
    onError: (error) => showErrorFromMutation(error, "Failed to select custom query"),
  });
};

export const useCreateAccount = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAccountApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-similar-account-targets"],
      });
      queryClient.invalidateQueries({ queryKey: ["custom-account-queries"] });
      showSuccess(
        data.length === 1
          ? "Account research started!"
          : `Research started for ${data.length} accounts!`,
      );
      action?.(data);
    },
    onError: (error) =>
      showErrorFromMutation(error, "Failed to generate account"),
  });
};

export const useCreateAccountFromContacts = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAccountFromContactsApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-accounts"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-similar-account-targets"],
      });
      showSuccess("Account research started!");
      action?.(data);
    },
    onError: (error) =>
      showErrorFromMutation(error, "Failed to create account"),
  });
};

export const useUpdateAccountStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAccountStatusApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      showSuccess("Account status updated!");
    },
    onError: (error) =>
      showErrorFromMutation(error, "Failed to update account status"),
  });
};

// fetch company info hook
export const useFetchCompanyInfo = ({userId = "", companyName = "", enabled = true}) => {
    return useQuery({
    queryKey: ["companyInfo", userId, companyName],
    queryFn: async () => {
      const data = await fetchCompanyInfoApi({userId, companyName});
      return convertCurrenciesDeep(data);
    },
    enabled: !!userId && !!companyName && enabled,
  });
};

export const useAccountRevenue = ({
  userId = "",
  companyName = "",
  companyDomain = "",
  enabled = true,
}) =>
  useQuery({
    queryKey: ["accountRevenue", userId, companyName],
    queryFn: () => fetchAccountRevenueApi({ userId, companyName, companyDomain }),
    enabled: Boolean(userId && companyName) && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 0,
  });

// fetch account dashboard hook
export const useFetchAccountDashboard = ({ companyName = "", enabled = true }) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  return useQuery({
    queryKey: ["accountDashboard", companyName, timeZone],
    queryFn: () => fetchAccountDashboardApi({ companyName, timeZone }),
    enabled: !!companyName && enabled,
    staleTime: 0,
  });
};

const FOLLOW_UP_REVIEW_RUNNING = new Set(["queued", "running"]);

export const useFollowUpAlert = ({ companyName = "", enabled = true }) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["accountFollowUpAlert", companyName],
    queryFn: () => fetchFollowUpAlertApi({ companyName }),
    enabled: !!companyName && enabled,
    refetchInterval: ({ state }) =>
      FOLLOW_UP_REVIEW_RUNNING.has(state.data?.last_run_status) ? 5000 : false,
    // The toggle hides itself when this fails; a toast on every poll would be noise.
    meta: { silentError: true },
  });

  const status = query.data?.last_run_status;
  const previousStatus = useRef(status);
  useEffect(() => {
    if (
      FOLLOW_UP_REVIEW_RUNNING.has(previousStatus.current) &&
      !FOLLOW_UP_REVIEW_RUNNING.has(status)
    ) {
      // A review just finished: load its new suggestions.
      queryClient.invalidateQueries({ queryKey: ["accountDashboard"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-accounts"] });
    }
    previousStatus.current = status;
  }, [status, queryClient]);

  return query;
};

export const useSetFollowUpAlert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setFollowUpAlertApi,
    onSuccess: (data, { companyName, enabled }) => {
      queryClient.setQueryData(["accountFollowUpAlert", companyName], data);
      showSuccess(
        enabled ? "Follow-up alerts turned on" : "Follow-up alerts turned off",
        enabled
          ? "Reviewing this account's conversations now, then every 4 days."
          : undefined,
      );
    },
    onError: (error) =>
      showErrorFromMutation(error, "Failed to update follow-up alerts"),
  });
};

export const useDismissFollowUpSuggestion = ({ action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dismissFollowUpSuggestionApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["accountDashboard"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-accounts"] });
      showSuccess("Suggestion deleted");
      action?.(data);
    },
    onError: (error) =>
      showErrorFromMutation(error, "Failed to delete suggestion"),
  });
};

export const useFetchAccountDeals = ({ companyIdentifier = "", enabled = true }) =>
  useQuery({
    queryKey: ["accountDeals", companyIdentifier],
    queryFn: () => fetchAccountDealsApi({ companyIdentifier }),
    enabled: Boolean(companyIdentifier) && enabled,
  });

export const useCreateAccountDeal = ({ companyIdentifier, action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deal) => createAccountDealApi({ companyIdentifier, ...deal }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["accountDeals", companyIdentifier],
      });
      showSuccess("Deal created successfully!");
      action?.(data);
    },
    onError: (error) => showErrorFromMutation(error, "Failed to create deal"),
  });
};

export const useUpdateAccountDeal = ({ companyIdentifier, action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAccountDealApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["accountDeals", companyIdentifier],
      });
      showSuccess("Deal updated successfully!");
      action?.(data);
    },
    onError: (error) => showErrorFromMutation(error, "Failed to update deal"),
  });
};

export const useDeleteAccountDeal = ({ companyIdentifier, action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAccountDealApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["accountDeals", companyIdentifier],
      });
      showSuccess("Deal deleted successfully!");
      action?.(data);
    },
    onError: (error) => showErrorFromMutation(error, "Failed to delete deal"),
  });
};

const accountDealNotesKey = (dealId) => ["accountDealNotes", dealId];

export const useFetchAccountDealNotes = ({ dealId = "" }) =>
  useQuery({
    queryKey: accountDealNotesKey(dealId),
    queryFn: () => fetchAccountDealNotesApi({ dealId }),
    enabled: Boolean(dealId),
  });

export const useCreateAccountDealNote = ({ dealId, action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note) => createAccountDealNoteApi({ dealId, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountDealNotesKey(dealId) });
      showSuccess("Note added successfully!");
      action?.();
    },
    onError: (error) => showErrorFromMutation(error, "Failed to add note"),
  });
};

export const useUpdateAccountDealNote = ({ dealId, action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, note }) =>
      updateAccountDealNoteApi({ noteId, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountDealNotesKey(dealId) });
      showSuccess("Note updated successfully!");
      action?.();
    },
    onError: (error) => showErrorFromMutation(error, "Failed to update note"),
  });
};

export const useDeleteAccountDealNote = ({ dealId, action } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId }) => deleteAccountDealNoteApi({ noteId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountDealNotesKey(dealId) });
      showSuccess("Note deleted successfully!");
      action?.();
    },
    onError: (error) => showErrorFromMutation(error, "Failed to delete note"),
  });
};

// delete account hook
export const useDeleteAccount = ({enabled = true, action}) => {
  const queryClient = useQueryClient();
    return useMutation({
    mutationFn: ({user_id, company_name}) => deleteAccountApi({user_id, company_name}),
    onSuccess: () => {
      showSuccess("Account deleted successfully!");
      if (action) {
        action();
      }
      queryClient.invalidateQueries({
        queryKey: ["accounts"],
      });
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to delete account");
    },
    enabled: enabled,
  });
};

export const useBulkDeleteAccounts = ({ action } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ user_id, company_names = [] }) => {
      const results = await Promise.allSettled(
        company_names.map((company_name) =>
          deleteAccountApi({ user_id, company_name }),
        ),
      );
      const failed = results.filter((result) => result.status === "rejected");

      if (failed.length > 0) {
        throw new Error(
          `Failed to delete ${failed.length} of ${company_names.length} accounts.`,
        );
      }

      return results;
    },
    onSuccess: (_, variables) => {
      showSuccess(`${variables.company_names.length} accounts deleted successfully!`);
      action?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-similar-account-targets"],
      });
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to delete accounts");
    },
  });
};
// fetch account action plan hook
export const useFetchAccountActionPlan = ({
  userId = "",
  organizationId = "",
  companyName = "",
  enabled = true,
}) => {
    return useQuery({
    queryKey: ["accountActionPlan", userId, organizationId, companyName],
    queryFn: () => fetchAccountActionPlanApi({userId, organizationId, companyName}),
    enabled: !!userId && !!organizationId && !!companyName && enabled,
    // The action plan is LLM-generated (a 20-60s call when uncached upstream), so
    // refetching it on every mount was the most expensive of these overrides.
    staleTime: 60_000,
  });
};

// update account action plan hook
export const useUpdateAccountActionPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ actionId, userId, owner, dueDate, status }) =>
      updateAccountActionPlanApi({
        actionId,
        userId,
        owner,
        dueDate,
        status,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["accountActionPlan"],
        exact: false,
      });
      showSuccess("Action updated successfully!");
      return data;
    },
    onError: (error) => {
      showErrorFromMutation(error, error.message || "Failed to update action");
    },
  });
};

// whitespacing account action plan hook
export const useWhitespacingAccountActionPlan = ({userId = "", companyName = "", enabled = true}) => {
    return useQuery({
        queryKey: ["whitespacingAccountActionPlan", userId, companyName],
        queryFn: () => whitespacingAccountActionPlanApi({userId, companyName}),
        enabled: !!userId && !!companyName && enabled,
        // Each retry would start another multi-minute analysis on the backend.
        retry: false,
      });
};

// log won/lost for a whitespace card hook
export const useLogWhitespaceOutcome = ({ userId = "", companyName = "" } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logWhitespaceOutcomeApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["whitespacingAccountActionPlan", userId, companyName],
      });
      showSuccess(`Marked as ${data?.outcome === "won" ? "won" : "lost"}.`);
    },
    onError: (error) => showErrorFromMutation(error, "Failed to log outcome"),
  });
};

// save edited vendors for a whitespace card hook
export const useUpdateWhitespaceVendors = ({ userId = "", companyName = "" } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateWhitespaceVendorsApi,
    onSuccess: (data) => {
      // Patch the cache instead of refetching: a refetch of a stale audit starts a multi-minute regeneration.
      queryClient.setQueryData(["whitespacingAccountActionPlan", userId, companyName], (old) =>
        old && {
          ...old,
          audits: old.audits.map((audit) =>
            audit.id === data.audit_id ? { ...audit, current_vendors: data.current_vendors } : audit,
          ),
        },
      );
      showSuccess("Vendors updated.");
    },
    onError: (error) => showErrorFromMutation(error, "Failed to save vendors"),
  });
};

// get chat answer hook
export const useGetChatAnswer = ({
  user_id = "",
  company_name = "",
  interactions_context = [],
  prompt = "",
  enabled = true,
  onStreamChunk,
}) => {
  return useQuery({
    queryKey: ["chatAnswer", user_id, company_name, prompt],
    queryFn: async () => {
      const stream = await getChatAnswerApi({
        user_id,
        prompt,
        company_name,
        interactions_context,
      });
      
      if (onStreamChunk && stream) {
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            
            // Keep the last incomplete line in buffer
            buffer = lines.pop() || "";

            // Process complete lines
            for (const line of lines) {
              if (line.trim()) {
                try {
                  const chunk = JSON.parse(line);
                  onStreamChunk(chunk);
                } catch (e) {
                  console.warn("Failed to parse chunk:", line);
                }
              }
            }
          }

          // Process remaining buffer
          if (buffer.trim()) {
            try {
              const chunk = JSON.parse(buffer);
              onStreamChunk(chunk);
            } catch (e) {
              console.warn("Failed to parse final chunk:", buffer);
            }
          }
        } finally {
          reader.releaseLock();
        }
      }

      return { streamed: true };
    },
    enabled: !!user_id && !!prompt && enabled,
  });
}

// --- Conversation-chat threads (multi-agent backend) ---

export const useConversationThreads = ({ userId, companyName, enabled = true }) =>
  useQuery({
    queryKey: ["conversationThreads", userId, companyName],
    queryFn: () =>
      fetchConversationThreads({ user_id: userId, company_name: companyName }),
    enabled: Boolean(userId) && enabled,
    retry: false,
  });

export const useConversationThreadMessages = ({
  userId,
  threadId,
  enabled = true,
}) =>
  useQuery({
    queryKey: ["conversationThreadMessages", userId, threadId],
    queryFn: () =>
      fetchConversationThreadMessages({ user_id: userId, thread_id: threadId }),
    enabled: Boolean(userId && threadId) && enabled,
    retry: false,
  });

export const useCreateConversationThread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createConversationThread,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["conversationThreads", variables.user_id],
        exact: false,
      });
    },
  });
};

export const useRenameConversationThread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: renameConversationThread,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["conversationThreads", variables.user_id],
        exact: false,
      });
    },
  });
};

export const useDeleteConversationThread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteConversationThread,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["conversationThreads", variables.user_id],
        exact: false,
      });
    },
  });
};

export const useSendConversationChatMessage = () =>
  useMutation({
    mutationFn: async ({ onStreamChunk, ...payload }) => {
      const stream = await sendConversationChatMessage(payload);
      if (!stream || !onStreamChunk) return { streamed: true };

      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (let line of lines) {
            line = line.trim();
            if (!line) continue;
            if (line.startsWith("data:")) {
              line = line.replace(/^data:\s*/, "");
            }
            if (line === "[DONE]" || !line) continue;
            try {
              onStreamChunk(JSON.parse(line));
            } catch {
              console.warn("Failed to parse chat chunk:", line);
            }
          }
        }
        if (buffer.trim()) {
          let line = buffer.trim();
          if (line.startsWith("data:")) {
            line = line.replace(/^data:\s*/, "");
          }
          if (line !== "[DONE]" && line) {
            try {
              onStreamChunk(JSON.parse(line));
            } catch {
              console.warn("Failed to parse final chat chunk:", buffer);
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
      return { streamed: true };
    },
  });

// fetch chat hook
export const useFetchChat = ({userId = "", enabled = true}) => {
  return useQuery({
    queryKey: ["chat", userId], 
    queryFn: async () => {
      const response = await fetchChatApi({userId});
      if (response) {
        const messageArray = response
          .split("\n")
          .map((obj) => {
            try {
              return JSON.parse(obj);
            } catch {
              return null;
            }
          })
          .filter((obj) => obj && Object.keys(obj).length > 0);
        return { messages: messageArray };
      }
      return { messages: [] };
    },
    enabled: !!userId && enabled,
  });
};
